(() => {
  if (window.WazupMobileStickyAtcLoaded) return;
  window.WazupMobileStickyAtcLoaded = true;

  const MOBILE_QUERY = '(max-width: 749px)';
  const ROOT_SELECTOR = '[data-wazup-mobile-sticky-atc]';
  const instances = new Map();

  class WazupMobileStickyAtc {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productInfo = root.closest('product-info');
      this.originalButton = document.getElementById(root.dataset.originalButtonId);
      this.stickyButton = root.querySelector('[data-wazup-sticky-atc-button]');
      this.label = root.querySelector('[data-wazup-sticky-atc-label]');
      this.price = root.querySelector('[data-wazup-sticky-atc-price]');
      this.variant = root.querySelector('[data-wazup-sticky-atc-variant]');
      this.priceSource = document.getElementById(`price-${this.sectionId}`);
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.originalButtonVisible = true;
      this.cookieBanner = null;

      if (!this.productInfo || !this.originalButton || !this.stickyButton) return;

      this.onStickyClick = this.onStickyClick.bind(this);
      this.onProductChange = this.onProductChange.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);

      this.stickyButton.addEventListener('click', this.onStickyClick);
      this.productInfo.addEventListener('change', this.onProductChange);
      this.mobileMedia.addEventListener('change', this.onMediaChange);

      this.buttonObserver = new MutationObserver(() => this.syncButton());
      this.buttonObserver.observe(this.originalButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
      });

      if (this.priceSource) {
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.priceSource, { childList: true, subtree: true });
      }

      this.cookieObserver = new MutationObserver(() => this.refreshVisibility());
      this.cookieDiscoveryObserver = new MutationObserver(() => this.connectCookieBanner());
      this.cookieDiscoveryObserver.observe(document.body, { childList: true });
      this.connectCookieBanner();

      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.originalButtonVisible = entry.isIntersecting;
          this.refreshVisibility();
        },
        { threshold: 0.01 }
      );
      this.visibilityObserver.observe(this.originalButton);

      this.syncAll();
      this.root.hidden = false;
    }

    syncAll() {
      this.syncButton();
      this.syncPrice();
      this.syncVariant();
    }

    syncButton() {
      const sourceLabel = this.originalButton.querySelector('span');
      const disabled =
        this.originalButton.disabled || this.originalButton.getAttribute('aria-disabled') === 'true';
      const loading = this.originalButton.classList.contains('loading');

      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (sourceLabel?.textContent.trim()) this.label.textContent = sourceLabel.textContent.trim();
    }

    syncPrice() {
      if (!this.priceSource || !this.price) return;
      const priceWrapper = this.priceSource.querySelector('.price');
      const priceSelector = priceWrapper?.classList.contains('price--on-sale')
        ? '.price__sale .price-item--sale'
        : '.price__regular .price-item--regular';
      const currentPrice = this.priceSource.querySelector(priceSelector);
      if (currentPrice?.textContent.trim()) this.price.textContent = currentPrice.textContent.trim();
    }

    syncVariant() {
      if (!this.variant) return;
      const picker = this.productInfo.querySelector('variant-selects');
      if (!picker) return;

      const selectedOptions = [
        ...picker.querySelectorAll('fieldset input[type="radio"]:checked, select'),
      ]
        .map((input) => input.value?.trim())
        .filter(Boolean);

      this.variant.textContent = selectedOptions.join(' · ');
    }

    onProductChange(event) {
      if (!event.target.closest('variant-selects')) return;
      requestAnimationFrame(() => this.syncVariant());
    }

    onStickyClick() {
      if (this.stickyButton.disabled) return;
      this.originalButton.click();
    }

    onMediaChange(event) {
      this.refreshVisibility();
    }

    connectCookieBanner() {
      const nextBanner = document.querySelector('.cc-window');
      if (nextBanner === this.cookieBanner) {
        this.refreshVisibility();
        return;
      }

      this.cookieObserver.disconnect();
      this.cookieBanner = nextBanner;

      if (this.cookieBanner) {
        this.cookieObserver.observe(this.cookieBanner, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }

      this.refreshVisibility();
    }

    isCookieBannerVisible() {
      if (!this.cookieBanner || !document.contains(this.cookieBanner)) return false;
      if (
        this.cookieBanner.classList.contains('cc-invisible') ||
        this.cookieBanner.getAttribute('aria-hidden') === 'true'
      ) {
        return false;
      }
      const style = window.getComputedStyle(this.cookieBanner);
      const bounds = this.cookieBanner.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        bounds.width > 0 &&
        bounds.height > 0
      );
    }

    refreshVisibility() {
      this.setVisible(
        this.mobileMedia.matches && !this.originalButtonVisible && !this.isCookieBannerVisible()
      );
    }

    setVisible(visible) {
      this.root.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.documentElement.classList.toggle('wazup-mobile-sticky-atc-visible', visible);
    }

    destroy() {
      this.setVisible(false);
      this.stickyButton?.removeEventListener('click', this.onStickyClick);
      this.productInfo?.removeEventListener('change', this.onProductChange);
      this.mobileMedia?.removeEventListener('change', this.onMediaChange);
      this.buttonObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.cookieObserver?.disconnect();
      this.cookieDiscoveryObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new WazupMobileStickyAtc(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
